import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
  ProgressBar,
  makeStyles,
  tokens,
  Text,
} from '@fluentui/react-components'
import { CheckmarkCircle24Regular, ErrorCircle24Regular } from '@fluentui/react-icons'

const useStyles = makeStyles({
  surface: {
    minWidth: '500px',
    maxWidth: '600px',
    borderTop: `4px solid ${tokens.colorBrandForeground1}`,
  },
  title: {
    fontSize: '20px',
    fontWeight: 600,
    marginBottom: '8px',
  },
  content: {
    paddingTop: '16px',
    paddingBottom: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  progressInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  currentProject: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
    marginBottom: '4px',
  },
  progressCounter: {
    fontSize: '14px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  summary: {
    marginTop: '16px',
    padding: '12px',
    borderRadius: '4px',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  successMessage: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
    color: tokens.colorPaletteGreenForeground1,
    fontWeight: 600,
  },
  failureList: {
    marginTop: '12px',
    maxHeight: '200px',
    overflowY: 'auto',
  },
  failureItem: {
    padding: '8px',
    marginBottom: '8px',
    backgroundColor: '#fde7e9',
    borderRadius: '4px',
    border: '1px solid #fec7ca',
  },
  failureProjectName: {
    fontWeight: 600,
    color: '#c50f1f',
    marginBottom: '4px',
  },
  failureError: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
  },
  actions: {
    marginTop: '16px',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
  },
})

interface BulkCompleteProgressModalProps {
  isOpen: boolean
  onClose: () => void
  isProcessing: boolean
  currentIndex: number
  totalCount: number
  currentProjectName: string
  successCount: number
  failures: Array<{ projectName: string; error: string }>
  isComplete: boolean
  onCancel: () => void
}

export const BulkCompleteProgressModal = ({
  isOpen,
  onClose,
  isProcessing,
  currentIndex,
  totalCount,
  currentProjectName,
  successCount,
  failures,
  isComplete,
  onCancel,
}: BulkCompleteProgressModalProps) => {
  const styles = useStyles()

  const progress = totalCount > 0 ? (currentIndex + 1) / totalCount : 0

  const handleClose = () => {
    if (!isProcessing) {
      onClose()
    }
  }

  return (
    <Dialog open={isOpen} modalType="modal">
      <DialogSurface className={styles.surface}>
        <DialogTitle className={styles.title}>
          {isComplete ? 'Färdigställning klar' : 'Färdigställer rapporter'}
        </DialogTitle>
        <DialogBody>
          <DialogContent className={styles.content}>
            {isProcessing && (
              <>
                <div>
                  <div className={styles.progressInfo}>
                    <Text className={styles.currentProject}>
                      Bearbetar: {currentProjectName}
                    </Text>
                    <Text className={styles.progressCounter}>
                      {currentIndex + 1} / {totalCount}
                    </Text>
                  </div>
                  <ProgressBar value={progress} thickness="large" color="brand" />
                </div>
              </>
            )}

            {isComplete && (
              <div className={styles.summary}>
                <div className={styles.successMessage}>
                  <CheckmarkCircle24Regular />
                  <Text>
                    {successCount} av {totalCount} rapporter färdigställda
                  </Text>
                </div>

                {failures.length > 0 && (
                  <>
                    <Text>
                      <ErrorCircle24Regular
                        style={{
                          verticalAlign: 'middle',
                          marginRight: '4px',
                          color: '#c50f1f',
                        }}
                      />
                      {failures.length} rapport{failures.length > 1 ? 'er' : ''}{' '}
                      misslyckades:
                    </Text>
                    <div className={styles.failureList}>
                      {failures.map((failure, index) => (
                        <div key={index} className={styles.failureItem}>
                          <div className={styles.failureProjectName}>
                            {failure.projectName}
                          </div>
                          <div className={styles.failureError}>
                            {failure.error}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </DialogContent>

          <DialogActions className={styles.actions}>
            {isProcessing ? (
              <Button appearance="secondary" onClick={onCancel}>
                Avbryt
              </Button>
            ) : (
              <Button appearance="primary" onClick={handleClose}>
                Stäng
              </Button>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}
